// 🎞 **영상 표본 공급자** — 그리기 함수는 동기·순수이고, 영상 프레임(3D 자산 색·마스크 · 결과 카드 mp4)은 «미리 받아 둔 표본»으로만 본다.
//   ① `RecordingSource` 로 한 번 그려 어느 URL 의 어느 시각이 필요한지 모은다 → ② 비동기로 표본을 채운다 → ③ 진짜로 그린다.
//   덕분에 서브프레임(모션 블러)도 «진짜 다른 시각의 프레임»을 잡는다(구슬은 60fps 로 구웠다).
//
// ★mediabunny `CanvasSink.getCanvas(t)` 를 쓴다★ — 이 레포는 이미 10곳이 mediabunny 를 직접 import 한다(합본 `mediabunnyRender` 등).
//   착수문의 «명시 의존성 문의»는 그 실측으로 해소됐다(package.json 에는 @remotion/media·web-renderer 의 고정 의존성 1.27.2 로 있다).
// ★비트맵은 콜백 뒤 닫힌다(0단계 함정 ②)★ — 여기서는 `getCanvas` 가 돌려준 캔버스를 **즉시 복사**해 보관한다(풀에서 재사용되기 전에).

export interface FrameSource {
  /** `url` 영상의 `tSec` 프레임. 없으면 null(그 층은 이 프레임에서 그리지 않는다). */
  video(url: string, tSec: number): CanvasImageSource | null;
}

/** 시각 키 — 1/240 초 단위로 반올림(30fps × 서브프레임 8 까지 구분 · 같은 표본은 한 번만 받는다). */
export const sampleKey = (url: string, tSec: number): string => `${url}@${Math.round(tSec * 240)}`;

/** ① 필요 목록만 모으는 공급자. 그림은 안 그려진다(항상 null). */
export class RecordingSource implements FrameSource {
  readonly needs = new Map<string, { url: string; tSec: number }>();
  video(url: string, tSec: number): null {
    const k = sampleKey(url, tSec);
    if (!this.needs.has(k)) this.needs.set(k, { url, tSec: Math.round(tSec * 240) / 240 });
    return null;
  }
}

/** ③ 채워진 표본으로 그리는 공급자. */
export class MapSource implements FrameSource {
  constructor(private readonly frames: ReadonlyMap<string, CanvasImageSource>) {}
  video(url: string, tSec: number): CanvasImageSource | null {
    return this.frames.get(sampleKey(url, tSec)) ?? null;
  }
}

/** 테스트·검사용 — 아무 영상도 없다. */
export const EMPTY_SOURCE: FrameSource = { video: () => null };

type Sink = { getCanvas(t: number): Promise<{ canvas: HTMLCanvasElement | OffscreenCanvas; timestamp: number; duration: number } | null> };
type Mediabunny = typeof import("mediabunny");

const sinkCache = new Map<string, Promise<Sink | null>>();
let mediabunnyMod: Promise<Mediabunny> | null = null;

async function sinkFor(url: string): Promise<Sink | null> {
  let hit = sinkCache.get(url);
  if (!hit) {
    hit = (async () => {
      mediabunnyMod ??= import("mediabunny");
      const mb = await mediabunnyMod;
      const input = new mb.Input({ source: new mb.UrlSource(url), formats: mb.ALL_FORMATS });
      const track = await input.getPrimaryVideoTrack();
      if (!track) return null;
      return new mb.CanvasSink(track, { poolSize: 4 }) as unknown as Sink;
    })().catch((e) => { console.warn("[promo-reel] 영상 표본 준비 실패", url, e); return null; });
    sinkCache.set(url, hit);
  }
  return hit;
}

/** 표본 복사 — 풀 캔버스가 재사용되기 전에 우리 캔버스로 옮긴다. */
function copyCanvas(src: HTMLCanvasElement | OffscreenCanvas): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = src.width; c.height = src.height;
  c.getContext("2d")?.drawImage(src, 0, 0);
  return c;
}

/**
 * ② 필요 목록을 채운다. 같은 URL 의 표본은 시각 순으로 차례로 받는다(디코더는 앞으로만 가는 게 빠르다).
 *   실패한 표본은 비워 둔다 — 그 층만 이 프레임에서 빠지고 렌더는 계속된다(검은 프레임이 아니라 층 하나 누락 · 로그로 남긴다).
 */
export async function fillSamples(needs: Iterable<{ url: string; tSec: number }>): Promise<Map<string, CanvasImageSource>> {
  const byUrl = new Map<string, number[]>();
  for (const n of needs) {
    const arr = byUrl.get(n.url) ?? [];
    arr.push(n.tSec);
    byUrl.set(n.url, arr);
  }
  const out = new Map<string, CanvasImageSource>();
  await Promise.all([...byUrl].map(async ([url, times]) => {
    const sink = await sinkFor(url);
    if (!sink) return;
    times.sort((a, b) => a - b);
    for (const t of times) {
      try {
        const got = await sink.getCanvas(t);
        if (got) out.set(sampleKey(url, t), copyCanvas(got.canvas));
      } catch (e) {
        console.warn("[promo-reel] 표본 실패", url, t, e);
      }
    }
  }));
  return out;
}

/** 캐시 비우기(스튜디오에서 다른 스펙으로 갈아탈 때). */
export function resetFrameSources(): void {
  sinkCache.clear();
}
